// Archivo: /web/src/App.jsx
// Descripción: App con gestión real de usuarios y páginas básicas para roles/módulos

import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ModuleProtectedRoute from './components/ModuleProtectedRoute';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import './App.css';

import ErrorBoundary from '@/components/ErrorBoundary';


// Lazy loading de coatalogos
const PacientesList = React.lazy(() => import('./pages/pacientes'));
const ReligionesList = React.lazy(() => import('./pages/catalogos/religiones'));
const NacionalidadesList = React.lazy(() => import('./pages/catalogos/nacionalidades'));
const IdentificacionesList = React.lazy(() => import('./pages/catalogos/identificaciones'));
const ParentescosList = React.lazy(() => import('./pages/catalogos/parentescos'));
const EstadoCivilList = React.lazy(() => import('./pages/catalogos/estadocivil'));
const OcupacionesList = React.lazy(() => import('./pages/catalogos/ocupaciones'));
const EstudiosLabList = React.lazy(() => import('./pages/catalogos/estudioslab'));
const DiagnosticosList = React.lazy(() => import('./pages/catalogos/diagnosticos'));
const TratamientosList = React.lazy(() => import('./pages/catalogos/tratamientos'));
const GPCList = React.lazy(() => import('./pages/catalogos/gpc'));
const EspecialidadesMedicasList = React.lazy(() => import('./pages/catalogos/especialidadesmedicas'));
const AntecedentesHeredoFamiliaresLista = React.lazy(() => import('./pages/catalogos/antecedentesheredofamiliares'));
const AntecedentesNoPatList = React.lazy(() => import('./pages/catalogos/antecedentesnopat'));
const EstudiosPreviosList = React.lazy(() => import('./pages/catalogos/estudiosprevios'));
const ExploracionFisicaList = React.lazy(() => import('./pages/catalogos/exploracionfisica'));
const PronosticosList = React.lazy(() => import('./pages/catalogos/pronosticos'));
const ProcedimientosList = lazy(() => import('./pages/catalogos/procedimientos'));
const VacunasList = lazy(() => import('./pages/catalogos/vacunas'));
const AlergiasList = lazy(() => import('./pages/catalogos/alergias'));
const TiposTratamientoList = lazy(() => import('./pages/catalogos/tipostratamiento')); 


//Clínicos
const NotaIngresoHospitalarioList = lazy(() => import('./pages/clinicos/notaingresohospitalario'));
const HojaEnfermeriaList = lazy(() => import('./pages/clinicos/hoja-enfermeria'));
const ConsentimientoInformadoList = lazy(() => import('./pages/clinicos/consentimiento-informado'));
const NotaInterconsultaList = lazy(() => import('./pages/clinicos/notaInterconsulta'));
const NotasEvolucionList = lazy(() => import('./pages/clinicos/notasEvolucion'));
const NotasEgresoDefinitivaList = lazy(() => import('./pages/clinicos/notasEgresoDefinitiva'));
const NotasUrgenciaList = lazy(() => import('./pages/clinicos/notasUrgencia'));
const NotasDiariasHospitalizacionList = lazy(() => import('./pages/clinicos/notasDiariaHospitalizacion'));
const ReportesEstudiosAuxiliaresList = lazy(() => import('./pages/clinicos/reportesEstudiosAuxiliares'));

const CitasList = React.lazy(() => import('./pages/citas'));
const MedicosList = React.lazy(() => import('./pages/medicos'));
const ExpedientesList = React.lazy(() => import('./pages/atencion/expedientes'));
const DocumentosClinicosDashboard = React.lazy(() => import('./pages/atencion/expedientes/DocumentosClinicosDashboard'));
const ExpedientePage = React.lazy(() => import('./pages/atencion/expedientes/ExpedientePage'));


const TiposSangreList = React.lazy(() => import('./pages/catalogos/tipossangre'));
const TiposSangreISBTList = React.lazy(() => import('./pages/catalogos/tipossangreisbt'));

3
// COMPONENTES ADMINISTRATIVOS 
const UsuariosList = React.lazy(() => import('./pages/usuarios'));
const RolesList = React.lazy(() => import('./pages/roles'));
const ModulosCRUD = React.lazy(() => import('./components/ModulosCRUD'));


// Componente de Loading
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  }}>
    <div style={{
      width: '60px',
      height: '60px',
      border: '4px solid rgba(255, 255, 255, 0.3)',
      borderTop: '4px solid #4299E1',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* Ruta pública */}
                <Route path="/login" element={<Login />} />
                
                {/* Rutas protegidas principales */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />

                {/* 🏥 MÓDULO PRINCIPAL - PACIENTES */}
                <Route path="/pacientes" element={
                  <ProtectedRoute>
                    <ModuleProtectedRoute requiredModule="pacientes">
                      <PacientesList />
                    </ModuleProtectedRoute>
                  </ProtectedRoute>
                } />

                <Route path="/clinicos" element={
                <ModuleProtectedRoute>
                  <Suspense fallback={<div>Cargando módulos clínicos...</div>}>
                    <div>Dashboard de Módulos Clínicos</div>
                  </Suspense>
                </ModuleProtectedRoute>
                } />

                <Route path="/atencion/expedientes" element={
                  <ProtectedRoute>
                    <ModuleProtectedRoute requiredModule="expediente">
                      <ExpedientesList />
                    </ModuleProtectedRoute>
                  </ProtectedRoute>
                } />
                <Route path="/atencion/expediente/:id" element={
                  <ProtectedRoute>
                    <ModuleProtectedRoute requiredModule="expediente">
                      <ExpedientePage />
                    </ModuleProtectedRoute>
                  </ProtectedRoute>
                } />

                <Route path="/atencion/expedientes/documentos/:pacienteId" element={
                  <ProtectedRoute>
                    <ModuleProtectedRoute requiredModule="expediente">
                      <DocumentosClinicosDashboard />
                    </ModuleProtectedRoute>
                  </ProtectedRoute>
                } />

                {/* 📚 CATÁLOGOS DEL SISTEMA */}
                <Route path="/catalogos/religiones" element={
                  <ProtectedRoute>
                    <ModuleProtectedRoute moduleName="catalogos">
                      <ReligionesList />
                    </ModuleProtectedRoute>
                  </ProtectedRoute>
                } />

                <Route path="/catalogos/nacionalidades" element={
                  <ProtectedRoute>
                    <ModuleProtectedRoute moduleName="catalogos">
                      <NacionalidadesList />
                    </ModuleProtectedRoute>
                  </ProtectedRoute>
                } />

                <Route path="/catalogos/identificaciones" element={
                  <ProtectedRoute>
                    <ModuleProtectedRoute moduleName="catalogos">
                      <IdentificacionesList />
                    </ModuleProtectedRoute>
                  </ProtectedRoute>
                } />

                <Route path="/catalogos/parentescos" element={
                  <ProtectedRoute>
                    <ModuleProtectedRoute moduleName="catalogos">
                      <ParentescosList />
                    </ModuleProtectedRoute>
                  </ProtectedRoute>
                } />

                <Route path="/catalogos/estadocivil" element={
                  <ProtectedRoute>
                    <ModuleProtectedRoute moduleName="catalogos">
                      <EstadoCivilList />
                    </ModuleProtectedRoute>
                  </ProtectedRoute>
                } />

                <Route path="/catalogos/ocupaciones" element={
                  <ProtectedRoute>
                    <ModuleProtectedRoute moduleName="catalogos">
                      <OcupacionesList />
                    </ModuleProtectedRoute>
                  </ProtectedRoute>
                } />

                {/* CATÁLOGOS DE TIPOS DE SANGRE */}
                <Route path="/catalogos/tipossangre" element={
                  <ProtectedRoute>
                    <ModuleProtectedRoute moduleName="catalogos">
                      <TiposSangreList />
                    </ModuleProtectedRoute>
                  </ProtectedRoute>
                } />

                {/* CATÁLOGOS DE ESTUDIOS DE LABORATORIO */}
                <Route path="/catalogos/estudioslab" element={
                  <ProtectedRoute>
                    <ModuleProtectedRoute moduleName="catalogos">
                      <EstudiosLabList />
                    </ModuleProtectedRoute>
                  </ProtectedRoute>
                } />

                <Route path="/catalogos/tipossangre-isbt" element={
                  <ProtectedRoute>
                    <ModuleProtectedRoute requiredModule="catalogos">
                      <TiposSangreISBTList />
                    </ModuleProtectedRoute>
                  </ProtectedRoute>
                } />

                <Route path="/catalogos/diagnosticos" element={
                  <ModuleProtectedRoute>
                    <DiagnosticosList />
                  </ModuleProtectedRoute>
                } />

                <Route path="/catalogos/gpc" element={
                  <ModuleProtectedRoute>
                    <GPCList />
                  </ModuleProtectedRoute>
                } />

                <Route path="/catalogos/tratamientos" element={
                  <ModuleProtectedRoute>
                    <TratamientosList />
                  </ModuleProtectedRoute>
                } />

                <Route path="/catalogos/especialidadesmedicas" element={
                  <ModuleProtectedRoute>
                    <EspecialidadesMedicasList />
                  </ModuleProtectedRoute>
                } />

                <Route path="/catalogos/antecedentes-heredo-familiares" element={
                  <ModuleProtectedRoute>
                    <AntecedentesHeredoFamiliaresLista />
                  </ModuleProtectedRoute>
                } />
                <Route path="/catalogos/antecedentesnopat" element={
                  <ModuleProtectedRoute>
                    <AntecedentesNoPatList />
                  </ModuleProtectedRoute>
                } />
                <Route path="/catalogos/estudiosprevios" element={
                  <ModuleProtectedRoute>
                    <EstudiosPreviosList />
                  </ModuleProtectedRoute>
                } />
                <Route path="/catalogos/exploracionfisica" element={
                  <ModuleProtectedRoute>
                    <ExploracionFisicaList />
                  </ModuleProtectedRoute>
                } />
                <Route path="/catalogos/pronosticos" element={
                  <ModuleProtectedRoute requiredModule="pronosticos">
                    <PronosticosList />
                  </ModuleProtectedRoute>
                } />
                <Route path="/catalogos/procedimientos" element={
                  <ModuleProtectedRoute>
                    <ProcedimientosList />
                  </ModuleProtectedRoute>
                } />
                <Route path="/catalogos/vacunas" element={
                  <ModuleProtectedRoute>
                    <VacunasList />
                  </ModuleProtectedRoute>
                } />
                <Route path="/catalogos/alergias" element={
                  <ModuleProtectedRoute>
                    <AlergiasList />
                  </ModuleProtectedRoute>
                } />

                <Route path="/catalogos/tipostratamiento" element={
                  <ModuleProtectedRoute>
                    <TiposTratamientoList />
                  </ModuleProtectedRoute>
                } />

                {/* ⚙️ RUTAS FUNCIONALIDADES */}

                <Route path="/citas" element={
                  <ModuleProtectedRoute>
                    <CitasList />
                  </ModuleProtectedRoute>
                } />
                <Route path="/medicos" element={
                  <ModuleProtectedRoute>
                    <MedicosList />
                  </ModuleProtectedRoute>
                } />

                {/* Rutas de Módulos Clínicos */}

                <Route path="/clinicos/notaingresohospitalario" element={
                  <ModuleProtectedRoute>
                    <Suspense fallback={<div>Cargando módulo clínico...</div>}>
                      <NotaIngresoHospitalarioList />
                    </Suspense>
                  </ModuleProtectedRoute>
                } />
                <Route 
                  path="/clinicos/nota-interconsulta" 
                  element={
                    <ModuleProtectedRoute>
                      <NotaInterconsultaList />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route path="/clinicos/hoja-enfermeria" 
                  element={
                    <ModuleProtectedRoute>
                      <HojaEnfermeriaList />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route path="/clinicos/consentimiento-informado" 
                  element={
                    <ModuleProtectedRoute>
                      <ConsentimientoInformadoList />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route path="/clinicos/notas-evolucion" 
                  element={
                    <ModuleProtectedRoute>
                      <NotasEvolucionList />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route path="/clinicos/notas-egreso-definitivo" 
                  element={
                    <ModuleProtectedRoute>
                      <NotasEgresoDefinitivaList />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route path="/clinicos/notas-urgencia" 
                  element={
                    <ModuleProtectedRoute>
                      <NotasUrgenciaList />
                    </ModuleProtectedRoute>
                  } 
                />
                <Route path="/clinicos/notas-diaria-hospitalizacion"
                 element={
                   <ModuleProtectedRoute>
                     <NotasDiariasHospitalizacionList />
                   </ModuleProtectedRoute>
                 } />
                <Route path="/clinicos/reportes-estudios-auxiliares" element={
                  <ModuleProtectedRoute>
                    <ReportesEstudiosAuxiliaresList />
                  </ModuleProtectedRoute>
                } />

                {/* RUTAS ADMINISTRATIVAS */}


                {/* GESTIÓN DE USUARIOS */}
                <Route path="/admin/usuarios" element={<Navigate to="/usuarios" replace />} />
                <Route 
                  path="/usuarios" 
                  element={
                    <ModuleProtectedRoute requireAdmin={true}>
                      <UsuariosList />
                    </ModuleProtectedRoute>
                  } />

                {/* 🔑 GESTIÓN DE ROLES  */}
                <Route path="/admin/roles" element={<Navigate to="/roles" replace />} />
                  <Route 
                    path="/roles" 
                    element={
                      <ProtectedRoute>
                        <ModuleProtectedRoute requireAdmin={true}>
                          <RolesList />
                        </ModuleProtectedRoute>
                      </ProtectedRoute>
                    } 
                  />

                {/* ⚙️ GESTIÓN DE MÓDULOS  */}
                <Route path="/admin/modulos" element={
                  <ProtectedRoute>
                    <ModuleProtectedRoute moduleName="admin" requireAdmin={true}>
                      <ModulosCRUD />
                    </ModuleProtectedRoute>
                  </ProtectedRoute>
                } />

                {/* Ruta por defecto - redirigir a login */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;